/**
 * Chat Suggestions Component
 * Displays clickable suggestion buttons in a grid layout
 *
 * Accessibility:
 * - Semantic navigation landmark
 * - Proper button labeling
 * - Respects reduced motion preferences
 */

import React from 'react';
import { motion } from 'motion/react';
import { cn } from '../../../../lib/utils';
import type { ChatSuggestion } from '../types/chat.types';
import { SuggestionButton } from './SuggestionButton';
import { OffTopicIndicator } from './OffTopicIndicator';
import { getOffTopicHeader, getSuggestionsLabel } from '../utils/suggestionCopy';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';

interface ChatSuggestionsProps {
  suggestions: ChatSuggestion[];
  isOffTopic?: boolean;
  onSuggestionClick: (suggestion: ChatSuggestion) => void;
  messageId: string;
  className?: string;
}

/**
 * Chat suggestions container
 * Displays suggestions as clickable buttons with header and indicator
 */
export const ChatSuggestions: React.FC<ChatSuggestionsProps> = ({
  suggestions,
  isOffTopic = false,
  onSuggestionClick,
  messageId,
  className,
}) => {
  const { prefersReducedMotion, tween } = useReducedMotion();

  // Don't render if no suggestions
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <motion.nav
      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
      transition={tween}
      className={cn(
        'mt-4 pt-4 border-t border-gray-200 dark:border-gray-700',
        'space-y-3',
        className
      )}
      aria-label="Suggested questions"
      role="navigation"
    >
      {/* Header with off-topic indicator */}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          {isOffTopic && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {getOffTopicHeader()}
            </p>
          )}
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {getSuggestionsLabel()}
          </p>
        </div>
        {isOffTopic && (
          <OffTopicIndicator size="sm" className="mt-0.5" />
        )}
      </div>

      {/* Suggestions grid */}
      <div
        className={cn(
          'grid gap-2.5',
          'grid-cols-1',
          'sm:grid-cols-2',
          'max-w-full'
        )}
        role="list"
      >
        {suggestions.map((suggestion, index) => (
          <div key={`${messageId}-suggestion-${index}`} role="listitem">
            <SuggestionButton
              suggestion={suggestion}
              onClick={onSuggestionClick}
              index={index}
            />
          </div>
        ))}
      </div>
    </motion.nav>
  );
};

export default ChatSuggestions;
